// src/pages/InteriorDesignPage.jsx
import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Phone, Mail, Check, ChevronRight,
  Sofa, Layers, PaintBucket, Home, Building2, Utensils,
  BedDouble, Star, Quote, ArrowRight,
} from 'lucide-react';

// ─── DATA ───────────────────────────────────────────────────────────────────

const SERVICES = [
  {
    icon: Home,
    title: 'Residential Interiors',
    desc: 'Complete home transformations — living rooms, bedrooms, kitchens & more, designed to reflect your personality.',
    color: '#c4a55a',
  },
  {
    icon: Building2,
    title: 'Commercial Spaces',
    desc: 'Office lobbies, co-working spaces, retail stores — designed for impact, functionality and brand identity.',
    color: '#8a7040',
  },
  {
    icon: Utensils,
    title: 'Modular Kitchen',
    desc: 'Smart, sleek modular kitchens with premium finishes, ergonomic layouts, and optimised storage.',
    color: '#c4a55a',
  },
  {
    icon: BedDouble,
    title: 'Bedroom Design',
    desc: 'Bespoke bedroom interiors with custom wardrobes, mood lighting, and luxurious material palettes.',
    color: '#8a7040',
  },
  {
    icon: Sofa,
    title: 'Furniture & Décor',
    desc: 'Custom furniture crafted to fit your space perfectly — from statement sofas to subtle accent pieces.',
    color: '#c4a55a',
  },
  {
    icon: PaintBucket,
    title: 'Colour & Texture',
    desc: 'Expert colour consulting, textured walls, wallpapers, and material selection to set the right mood.',
    color: '#8a7040',
  },
];

const PROCESS = [
  { step: '01', title: 'Consultation', desc: 'Free site visit and discussion of your vision, budget, and lifestyle needs.' },
  { step: '02', title: '3D Design', desc: 'Photo-realistic 3D renders so you see your space before a single nail is hit.' },
  { step: '03', title: 'Execution', desc: 'End-to-end project management by our skilled team — on time, on budget.' },
  { step: '04', title: 'Handover', desc: 'Final walk-through, finishing touches, and complete satisfaction guaranteed.' },
];

const TESTIMONIALS = [
  {
    name: 'Priya Sharma',
    location: 'Baner, Pune',
    rating: 5,
    text: 'Shree Sai transformed our 2BHK into something out of a magazine. The attention to detail and quality of work was outstanding.',
  },
  {
    name: 'Rahul Deshmukh',
    location: 'Wakad, Pune',
    rating: 5,
    text: 'Our office interior was completed ahead of schedule. The team was professional and the 3D previews were spot-on.',
  },
  {
    name: 'Meena Joshi',
    location: 'Kothrud, Pune',
    rating: 5,
    text: 'The modular kitchen they designed is functional, beautiful and exactly what I had imagined. Highly recommend!',
  },
];

// ─── UNSPLASH IMAGE CONFIGURATIONS ───────────────────────────────────────
const UNSPLASH_IMAGES = {
  // Hero section images
  hero: [
    'https://images.unsplash.com/photo-1618220179428-22790b461013?w=600&h=400&fit=crop',
    'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=600&h=400&fit=crop',
    'https://images.unsplash.com/photo-1556912172-45b7abe8b7e1?w=600&h=400&fit=crop',
    'https://images.unsplash.com/photo-1497366216548-37526070297c?w=600&h=400&fit=crop',
  ],
  
  // Gallery images
  gallery: {
    'living room': 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=600&h=400&fit=crop',
    'bedroom': 'https://images.unsplash.com/photo-1616594039964-ae9021a400a0?w=600&h=400&fit=crop',
    'kitchen': 'https://images.unsplash.com/photo-1556911220-bff31c812dba?w=600&h=400&fit=crop',
    'office': 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=600&h=400&fit=crop',
    'bathroom': 'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=600&h=400&fit=crop',
    'dining': 'https://images.unsplash.com/photo-1617806118233-18e1de247200?w=600&h=400&fit=crop',
  },
  
  // Service section images (backgrounds)
  services: [
    'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1497366216548-37526070297c?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1556911220-bff31c812dba?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1616594039964-ae9021a400a0?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1618220179428-22790b461013?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1584622650111-993a426fbf0a?w=400&h=300&fit=crop',
  ],
  
  // Process section images
  process: [
    'https://images.unsplash.com/photo-1581579438747-1dc8d17bbce4?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1581092335873-2a4c2c2b0a6b?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1581092160607-ee22621dd758?w=400&h=300&fit=crop',
    'https://images.unsplash.com/photo-1581091226825-a6a2a5aee158?w=400&h=300&fit=crop',
  ],
};

const GALLERY_ITEMS = [
  { label: 'Living Room', aspect: 'tall', bg: 'linear-gradient(160deg,#1a2030 0%,#2a1a0a 100%)' },
  { label: 'Bedroom',     aspect: 'wide', bg: 'linear-gradient(160deg,#0b2030 0%,#1a1030 100%)' },
  { label: 'Kitchen',     aspect: 'tall', bg: 'linear-gradient(160deg,#1a3322 0%,#0b1220 100%)' },
  { label: 'Office',      aspect: 'wide', bg: 'linear-gradient(160deg,#2a1020 0%,#0b1a30 100%)' },
  { label: 'Bathroom',    aspect: 'tall', bg: 'linear-gradient(160deg,#1a1a2a 0%,#2a0a0a 100%)' },
  { label: 'Dining',      aspect: 'wide', bg: 'linear-gradient(160deg,#2a2010 0%,#0a1a2a 100%)' },
];

const PACKAGES = [
  {
    name: 'Essential',
    price: '₹799',
    unit: '/sq.ft',
    features: ['Basic 2D floor plan', 'Paint & wallpaper', 'Modular furniture', 'Lighting plan', '6-month warranty'],
    highlight: false,
  },
  {
    name: 'Premium',
    price: '₹1,299',
    unit: '/sq.ft',
    features: ['3D visualisation', 'Custom furniture', 'False ceiling & lighting', 'Modular kitchen', 'Smart home basics', '1-year warranty'],
    highlight: true,
  },
  {
    name: 'Luxury',
    price: 'Custom',
    unit: 'quote',
    features: ['Full bespoke design', 'Premium materials', 'Imported furniture', 'Complete home automation', 'Dedicated designer', '2-year warranty'],
    highlight: false,
  },
];

// ─── HOOK: scroll-triggered animation ───────────────────────────────────────
function useReveal() {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.12 }
    );
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return [ref, visible];
}

// ─── REUSABLE SECTION COMPONENT ────────────────────────────────────────────────
function Section({ children, className = '', style = {} }) {
  const [ref, visible] = useReveal();
  return (
    <div
      ref={ref}
      className={className}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(32px)',
        transition: 'opacity .7s ease, transform .7s ease',
        ...style,
      }}
    >
      {children}
    </div>
  );
}

// ─── SERVICE CARD COMPONENT ─────────────────────────────────────────────
function ServiceCard({ service, index }) {
  const Icon = service.icon;
  const [ref, visible] = useReveal();
  
  return (
    <div
      ref={ref}
      className="group relative p-7 border border-[#c4a55a]/20 bg-white cursor-pointer hover:border-[#c4a55a]/60 transition-all duration-300 overflow-hidden"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity .6s ease ${index * 0.1}s, transform .6s ease ${index * 0.1}s, border-color .3s`,
      }}
    >
      {/* Background image with overlay */}
      <div className="absolute inset-0 opacity-0 group-hover:opacity-50 transition-opacity duration-500">
        <img 
          src={UNSPLASH_IMAGES.services[index % UNSPLASH_IMAGES.services.length]} 
          alt=""
          className="w-full h-full object-cover"
        />
      </div>
      
      {/* Corner accents */}
      <div className="absolute top-3 right-3 w-4 h-4 border-t border-r border-[#c4a55a]/30 group-hover:border-[#c4a55a]/70 transition-colors z-10" />
      <div className="absolute bottom-3 left-3 w-4 h-4 border-b border-l border-[#c4a55a]/30 group-hover:border-[#c4a55a]/70 transition-colors z-10" />

      <div
        className="w-12 h-12 flex items-center justify-center mb-5 border border-[#c4a55a]/25 group-hover:border-[#c4a55a]/60 transition-colors relative z-10"
        style={{ background: 'rgba(196,165,90,.06)' }}
      >
        <Icon size={20} color={service.color} />
      </div>

      <h3
        className="font-bold mb-3 relative z-10"
        style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'22px', color:'#0b1220' }}
      >
        {service.title}
      </h3>

      <p className="relative z-10" style={{ fontSize:'13px', color:'rgba(11,18,32,.55)', lineHeight:'1.7' }}>
        {service.desc}
      </p>

     
    </div>
  );
}

// ─── PROCESS STEP COMPONENT ─────────────────────────────────────────────
function ProcessStep({ step, index, isLast }) {
  const [ref, visible] = useReveal();
  
  return (
    <div
      ref={ref}
      className="relative p-7 border border-[#c4a55a]/15 overflow-hidden"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity .6s ease ${index * 0.15}s, transform .6s ease ${index * 0.15}s`,
        borderRight: !isLast ? '1px solid rgba(196,165,90,.15)' : '',
      }}
    >
      {/* Background image */}
      <div className="absolute inset-0 opacity-5">
        <img 
          src={UNSPLASH_IMAGES.process[index % UNSPLASH_IMAGES.process.length]} 
          alt=""
          className="w-full h-full object-cover"
        />
      </div>
      
      {/* Step number */}
      <div
        className="font-bold mb-5 relative z-10"
        style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'48px', color:'rgba(196,165,90,.18)', lineHeight:1 }}
      >
        {step.step}
      </div>

      {/* Connector arrow (desktop) */}
      {!isLast && (
        <div
          className="hidden lg:block absolute top-1/2 -right-4 z-10"
          style={{ color: '#c4a55a', opacity: .5 }}
        >
          <ChevronRight size={20} />
        </div>
      )}

      <h3
        className="font-bold mb-3 relative z-10"
        style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'22px', color:'#fff' }}
      >
        {step.title}
      </h3>

      <p className="relative z-10" style={{ fontSize:'13px', color:'rgba(255,255,255,.45)', lineHeight:'1.7' }}>
        {step.desc}
      </p>

      <div className="w-8 h-px mt-5 relative z-10" style={{ background:'#c4a55a' }} />
    </div>
  );
}

// ─── TESTIMONIAL CARD COMPONENT ─────────────────────────────────────────
function TestimonialCard({ testimonial, index }) {
  const [ref, visible] = useReveal();
  
  return (
    <div
      ref={ref}
      className="p-7 border border-[#c4a55a]/18"
      style={{
        background: 'rgba(255,255,255,.03)',
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(24px)',
        transition: `opacity .6s ease ${index * 0.15}s, transform .6s ease ${index * 0.15}s`,
      }}
    >
      <Quote size={20} color="#c4a55a" style={{ opacity:.5, marginBottom:'1rem' }} />

      <p style={{ fontSize:'13px', color:'rgba(255,255,255,.6)', lineHeight:'1.8', marginBottom:'1.5rem', fontStyle:'italic' }}>
        "{testimonial.text}"
      </p>

      {/* Stars */}
      <div className="flex gap-1 mb-4">
        {Array.from({ length: testimonial.rating }).map((_, s) => (
          <Star key={s} size={11} color="#c4a55a" fill="#c4a55a" />
        ))}
      </div>

      <div className="h-px mb-4" style={{ background:'rgba(196,165,90,.15)' }} />

      <div>
        <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'17px', fontWeight:600, color:'#fff' }}>{testimonial.name}</div>
        <div style={{ fontSize:'10px', fontWeight:700, letterSpacing:'.12em', textTransform:'uppercase', color:'rgba(196,165,90,.55)', marginTop:'2px' }}>{testimonial.location}</div>
      </div>
    </div>
  );
}

// ─── PACKAGE CARD COMPONENT ─────────────────────────────────────────────
function PackageCard({ pkg, index }) {
  const [ref, visible] = useReveal();
  
  return (
    <div
      ref={ref}
      className="relative p-8"
      style={{
        background: pkg.highlight ? '#0b1220' : '#fff',
        border: pkg.highlight ? '2px solid #c4a55a' : '1px solid rgba(196,165,90,.25)',
        opacity: visible ? 1 : 0,
        transform: visible ? (pkg.highlight ? 'scale(1.03) translateY(0)' : 'translateY(0)') : 'translateY(24px)',
        transition: `opacity .6s ease ${index * 0.15}s, transform .6s ease ${index * 0.15}s`,
      }}
    >
      {pkg.highlight && (
        <div
          className="absolute -top-3.5 left-1/2 -translate-x-1/2 px-4 py-1 text-[10px] font-extrabold tracking-[.2em] uppercase whitespace-nowrap"
          style={{ background: '#c4a55a', color: '#0b1220' }}
        >
          Most Popular
        </div>
      )}

      <div style={{ fontFamily:"'Cormorant Garamond',serif" }}>
        <div className="text-[10px] font-bold tracking-[.2em] uppercase mb-2"
             style={{ color: pkg.highlight ? 'rgba(196,165,90,.7)' : 'rgba(11,18,32,.4)' }}>
          {pkg.name}
        </div>
        <div className="flex items-end gap-1 mb-1">
          <span style={{ fontSize:'40px', fontWeight:700, lineHeight:1, color: pkg.highlight ? '#fff' : '#0b1220' }}>
            {pkg.price}
          </span>
        </div>
        <div className="text-[11px] font-semibold tracking-[.1em] uppercase mb-6"
             style={{ color: pkg.highlight ? 'rgba(196,165,90,.6)' : 'rgba(11,18,32,.35)' }}>
          {pkg.unit}
        </div>
      </div>

      <div className="h-px mb-6" style={{ background: pkg.highlight ? 'rgba(196,165,90,.25)' : 'rgba(11,18,32,.1)' }} />

      <ul className="space-y-3 mb-8">
        {pkg.features.map((f, j) => (
          <li key={j} className="flex items-start gap-3" style={{ fontSize:'13px' }}>
            <Check size={14} color="#c4a55a" style={{ flexShrink:0, marginTop:'2px' }} />
            <span style={{ color: pkg.highlight ? 'rgba(255,255,255,.7)' : 'rgba(11,18,32,.65)' }}>{f}</span>
          </li>
        ))}
      </ul>

      <a
        href="/inquiry"
        className="block w-full text-center py-3 font-extrabold tracking-[.18em] uppercase transition-opacity hover:opacity-80"
        style={{
          fontSize: '11px',
          background: pkg.highlight ? '#c4a55a' : 'transparent',
          color: pkg.highlight ? '#0b1220' : '#0b1220',
          border: pkg.highlight ? 'none' : '1px solid rgba(11,18,32,.2)',
        }}
      >
        Get Started
      </a>
    </div>
  );
}

// ─── SECTION HEADING ─────────────────────────────────────────────────────────
function SectionHeading({ eyebrow, title, subtitle, light = false }) {
  return (
    <div className="text-center mb-12">
      <div className="inline-flex items-center gap-3 mb-4">
        <div className="h-px w-8" style={{ background: '#c4a55a' }} />
        <span className="text-[10px] font-bold tracking-[.28em] uppercase" style={{ color: '#c4a55a' }}>
          {eyebrow}
        </span>
        <div className="h-px w-8" style={{ background: '#c4a55a' }} />
      </div>
      <h2
        className="font-bold leading-tight mb-3"
        style={{
          fontFamily: "'Cormorant Garamond', serif",
          fontSize: 'clamp(28px,4vw,44px)',
          color: light ? '#fff' : '#0b1220',
        }}
      >
        {title}
      </h2>
      {subtitle && (
        <p
          className="max-w-xl mx-auto leading-relaxed"
          style={{ fontSize: '14px', color: light ? 'rgba(255,255,255,.6)' : 'rgba(11,18,32,.55)' }}
        >
          {subtitle}
        </p>
      )}
    </div>
  );
}

// ─── PAGE ────────────────────────────────────────────────────────────────────
export default function InteriorDesignPage() {
  const [activeTab, setActiveTab] = useState('living');
  const [formData, setFormData] = useState({ name: '', phone: '', service: '', message: '' });

  const TABS = [
    { id: 'living',  label: 'Living Room' },
    { id: 'bedroom', label: 'Bedroom'     },
    { id: 'kitchen', label: 'Kitchen'     },
    { id: 'office',  label: 'Office'      },
  ];

  return (
    <div style={{ background: '#fff', fontFamily: "'Inter', sans-serif", overflowX: 'hidden' }}>

      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <div
        className="relative overflow-hidden flex items-center"
        style={{ minHeight: '90vh', background: '#0b1220' }}
      >
        {/* Decorative geometric background */}
        <div className="absolute inset-0" style={{ zIndex: 1 }}>
          {/* Large circle accent */}
          <div
            className="absolute"
            style={{
              width: '600px', height: '600px', borderRadius: '50%',
              border: '1px solid rgba(196,165,90,.08)',
              top: '-100px', right: '-100px',
            }}
          />
          <div
            className="absolute"
            style={{
              width: '400px', height: '400px', borderRadius: '50%',
              border: '1px solid rgba(196,165,90,.06)',
              top: '50px', right: '50px',
            }}
          />
          {/* Gold accent lines */}
          <div className="absolute bottom-0 left-0 w-full h-px" style={{ background: 'linear-gradient(to right,transparent,rgba(196,165,90,.4),transparent)' }} />
          <div className="absolute top-0 left-0 h-full w-px" style={{ background: 'linear-gradient(to bottom,transparent,rgba(196,165,90,.2),transparent)' }} />
        </div>

        {/* Content grid */}
        <div className="relative z-10 max-w-7xl mx-auto w-full px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">

          {/* Left: Text */}
          <div>
            <div className="inline-flex items-center gap-3 mb-6">
              <div className="w-6 h-px" style={{ background: '#c4a55a' }} />
              <span className="text-[10px] font-bold tracking-[.28em] uppercase" style={{ color: '#c4a55a' }}>
                Interior Design Services
              </span>
            </div>

            <h1
              className="font-bold leading-none mb-6 text-white"
              style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'clamp(40px,6vw,80px)' }}
            >
              Design Your
              <span className="block" style={{ color: '#c4a55a', fontStyle: 'italic' }}>Dream Space</span>
              With Us
            </h1>

            <p className="leading-relaxed mb-8 max-w-lg" style={{ fontSize:'15px', color:'rgba(255,255,255,.55)' }}>
              From concept to completion, our expert designers craft interiors that are beautiful,
              functional, and uniquely yours — across Pune and Maharashtra.
            </p>

            {/* Stats row */}
            <div className="flex flex-wrap gap-8 mb-10">
              {[['150+','Projects Done'],['8+','Years Experience'],['98%','Client Satisfaction']].map(([n,l]) => (
                <div key={l}>
                  <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'32px', fontWeight:700, color:'#c4a55a', lineHeight:1 }}>{n}</div>
                  <div style={{ fontSize:'10px', fontWeight:700, letterSpacing:'.15em', textTransform:'uppercase', color:'rgba(255,255,255,.35)', marginTop:'4px' }}>{l}</div>
                </div>
              ))}
            </div>

            <div className="flex flex-wrap gap-3">
              <a
                href="/inquiry"
                className="inline-flex items-center gap-2 px-8 py-3.5 font-extrabold tracking-[.18em] uppercase transition-opacity hover:opacity-85"
                style={{ background:'#c4a55a', color:'#0b1220', fontSize:'11px' }}
              >
                Get Free Consultation <ArrowRight size={14} />
              </a>
              <a
                href="#portfolio"
                className="inline-flex items-center gap-2 px-8 py-3.5 font-bold tracking-[.18em] uppercase text-white border border-white/25 hover:border-white/60 transition-colors"
                style={{ fontSize:'11px' }}
              >
                View Portfolio
              </a>
            </div>
          </div>

          {/* Right: Stylised image mosaic with Unsplash images */}
          <div className="relative hidden lg:grid grid-cols-2 gap-3" style={{ height: '480px' }}>
            {[
              { label:'Modern Living', h:'h-56', top:true, img: UNSPLASH_IMAGES.hero[0] },
              { label:'Luxury Bedroom', h:'h-40', top:false, img: UNSPLASH_IMAGES.hero[1] },
              { label:'Modular Kitchen', h:'h-40', top:true, img: UNSPLASH_IMAGES.hero[2] },
              { label:'Office Design', h:'h-56', top:false, img: UNSPLASH_IMAGES.hero[3] },
            ].map((item, i) => (
              <div
                key={i}
                className={`relative overflow-hidden ${item.h} ${item.top ? 'self-start' : 'self-end'}`}
                style={{ border: '1px solid rgba(196,165,90,.2)' }}
              >
                <img 
                  src={item.img} 
                  alt={item.label}
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-700"
                />
                <div
                  className="absolute inset-0 flex items-end p-3"
                  style={{ background: 'linear-gradient(to top,rgba(11,18,32,.7) 0%,transparent 60%)' }}
                >
                  <span style={{ fontSize:'10px', fontWeight:700, letterSpacing:'.12em', textTransform:'uppercase', color:'rgba(196,165,90,.8)' }}>
                    {item.label}
                  </span>
                </div>
                {/* Decorative corner */}
                <div className="absolute top-2 right-2 w-4 h-4 border-t border-r border-[#c4a55a]/40" />
                <div className="absolute bottom-2 left-2 w-4 h-4 border-b border-l border-[#c4a55a]/40" />
              </div>
            ))}
            {/* Floating badge */}
            <div
              className="absolute -bottom-4 left-1/2 -translate-x-1/2 px-5 py-3 border border-[#c4a55a]/40 whitespace-nowrap"
              style={{ background: 'rgba(11,18,32,.95)', animation: 'floatY 3s ease-in-out infinite' }}
            >
              <div style={{ fontSize:'9px', fontWeight:700, letterSpacing:'.15em', textTransform:'uppercase', color:'rgba(196,165,90,.65)' }}>Starting From</div>
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'20px', fontWeight:700, color:'#fff' }}>₹799 / sq.ft</div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 z-10" style={{ animation:'floatY 2s ease-in-out infinite' }}>
          <div style={{ fontSize:'9px', fontWeight:700, letterSpacing:'.2em', textTransform:'uppercase', color:'rgba(196,165,90,.5)' }}>Scroll</div>
          <div className="w-px h-8 bg-[#c4a55a]/30" />
        </div>
      </div>

      {/* ── SERVICES ─────────────────────────────────────────────────────── */}
      <div className="py-20 px-6" style={{ background: '#f8f5f0' }}>
        <Section>
          <SectionHeading
            eyebrow="What We Offer"
            title="Our Interior Design Services"
            subtitle="A complete spectrum of design services tailored to transform any space into an extraordinary experience."
          />
        </Section>

        <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {SERVICES.map((svc, i) => (
            <ServiceCard key={i} service={svc} index={i} />
          ))}
        </div>
      </div>

      {/* ── PROCESS ──────────────────────────────────────────────────────── */}
      <div className="py-20 px-6" style={{ background: '#0b1220' }}>
        <Section>
          <SectionHeading
            eyebrow="How We Work"
            title="Our Design Process"
            subtitle="A seamless, transparent process from your first call to the final handover."
            light
          />
        </Section>

        <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0">
          {PROCESS.map((p, i) => (
            <ProcessStep key={i} step={p} index={i} isLast={i === PROCESS.length - 1} />
          ))}
        </div>
      </div>

      {/* ── PORTFOLIO / GALLERY ──────────────────────────────────────────── */}
      <div id="portfolio" className="py-20 px-6" style={{ background: '#fff' }}>
        <Section>
          <SectionHeading
            eyebrow="Our Portfolio"
            title="Spaces We've Transformed"
            subtitle="A glimpse of our work across residential and commercial projects in Pune."
          />
        </Section>

        {/* Category tabs */}
        <div className="flex flex-wrap justify-center gap-2 mb-10">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className="px-5 py-2 font-bold tracking-[.15em] uppercase transition-all"
              style={{
                fontSize: '10px',
                background: activeTab === tab.id ? '#c4a55a' : 'transparent',
                color: activeTab === tab.id ? '#0b1220' : 'rgba(11,18,32,.45)',
                border: `1px solid ${activeTab === tab.id ? '#c4a55a' : 'rgba(11,18,32,.15)'}`,
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Gallery grid with Unsplash images */}
        <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-3 gap-3">
          {GALLERY_ITEMS.map((g, i) => {
            const imageKey = g.label.toLowerCase();
            const imageUrl = UNSPLASH_IMAGES.gallery[imageKey] || UNSPLASH_IMAGES.gallery['living room'];
            
            return (
              <div
                key={i}
                className="relative overflow-hidden group cursor-pointer border border-[#c4a55a]/20 hover:border-[#c4a55a]/50 transition-colors"
                style={{
                  height: g.aspect === 'tall' ? '240px' : '180px',
                }}
              >
                <img 
                  src={imageUrl}
                  alt={g.label}
                  className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                />
                <div
                  className="absolute inset-0 flex items-end p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                  style={{ background: 'linear-gradient(to top,rgba(11,18,32,.8) 0%,transparent 60%)' }}
                >
                  <span style={{ fontSize:'11px', fontWeight:700, letterSpacing:'.15em', textTransform:'uppercase', color:'#c4a55a' }}>
                    {g.label}
                  </span>
                </div>
                {/* Corner decor */}
                <div className="absolute top-2 left-2 w-5 h-5 border-t border-l border-[#c4a55a]/40" />
                <div className="absolute bottom-2 right-2 w-5 h-5 border-b border-r border-[#c4a55a]/40" />
                {/* Always visible label */}
                <div
                  className="absolute bottom-0 left-0 right-0 py-2 px-3 group-hover:opacity-0 transition-opacity"
                  style={{ background: 'rgba(11,18,32,.55)' }}
                >
                  <span style={{ fontSize:'10px', fontWeight:700, letterSpacing:'.12em', textTransform:'uppercase', color:'rgba(196,165,90,.8)' }}>
                    {g.label}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── PACKAGES ─────────────────────────────────────────────────────── */}
      <div className="py-20 px-6" style={{ background: '#f8f5f0' }}>
        <Section>
          <SectionHeading
            eyebrow="Pricing"
            title="Choose Your Package"
            subtitle="Transparent, all-inclusive packages with no hidden costs. Every project is unique — we'll tailor to your needs."
          />
        </Section>

        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          {PACKAGES.map((pkg, i) => (
            <PackageCard key={i} pkg={pkg} index={i} />
          ))}
        </div>
      </div>

      {/* ── TESTIMONIALS ─────────────────────────────────────────────────── */}
      <div className="py-20 px-6" style={{ background: '#0b1220' }}>
        <Section>
          <SectionHeading
            eyebrow="Client Stories"
            title="What Our Clients Say"
            light
          />
        </Section>

        <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-6">
          {TESTIMONIALS.map((t, i) => (
            <TestimonialCard key={i} testimonial={t} index={i} />
          ))}
        </div>
      </div>

      {/* ── WHY US BANNER ────────────────────────────────────────────────── */}
      <div className="py-14 px-6 border-t border-b border-[#c4a55a]/15" style={{ background: '#0f1826' }}>
        <div className="max-w-5xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-8 text-center">
          {[
            { icon: Layers, label:'In-House Team', sub:'No outsourcing' },
            { icon: Check,  label:'On-Time Delivery', sub:'Guaranteed' },
            { icon: Star,   label:'Premium Quality', sub:'ISI & branded materials' },
            { icon: Phone,  label:'24/7 Support', sub:'Always reachable' },
          ].map(({ icon: Icon, label, sub }, i) => (
            <div key={i}>
              <Icon size={22} color="#c4a55a" style={{ margin:'0 auto 12px' }} />
              <div style={{ fontFamily:"'Cormorant Garamond',serif", fontSize:'18px', fontWeight:600, color:'#fff' }}>{label}</div>
              <div style={{ fontSize:'10px', fontWeight:700, letterSpacing:'.12em', textTransform:'uppercase', color:'rgba(196,165,90,.5)', marginTop:'4px' }}>{sub}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── GLOBAL KEYFRAMES ─────────────────────────────────────────────── */}
      <style>{`
        @keyframes floatY {
          0%, 100% { transform: translateY(0); }
          50%       { transform: translateY(-8px); }
        }
      `}</style>
    </div>
  );
}